/**
 * Tax rates are entered and displayed as a percentage but stored as a decimal
 * fraction: 8.5% on screen is 0.085 in `organization.tax_rate`. The column is
 * NUMERIC(7,5), so the fraction carries at most five decimal places, which caps
 * a percentage at three — 10.125% is 0.10125, but 10.1254% has nowhere to go.
 */

/** Percentage input: at most two whole digits and three decimal places. */
const TAX_RATE_PERCENT_PATTERN = /^\d{1,2}(\.\d{1,3})?$/

/** Decimal places the fraction is stored with, matching NUMERIC(7,5). */
const FRACTION_DECIMAL_PLACES = 5

/** Blank is valid — the tax rate is optional. */
export const isTaxRatePercentValid = (raw: string): boolean => {
  const trimmed = raw.trim()
  return trimmed === '' || TAX_RATE_PERCENT_PATTERN.test(trimmed)
}

/**
 * Converts a percentage entry to the decimal fraction the API expects, as a
 * fixed-precision string.
 *
 * The string matters: dividing by 100 in binary floating point turns rates like
 * 4.225% into 0.042249999999999996, which the request schema rejects for
 * exceeding its digit budget. Rounding to the column's scale first keeps every
 * representable rate submittable.
 *
 * Returns `undefined` for blank or unusable input, so the field is omitted from
 * the request.
 */
export const taxRatePercentToFraction = (raw: string): string | undefined => {
  const trimmed = raw.trim()
  if (trimmed === '' || !TAX_RATE_PERCENT_PATTERN.test(trimmed)) {
    return undefined
  }
  const percent = Number(trimmed)
  if (!Number.isFinite(percent)) {
    return undefined
  }
  return (percent / 100).toFixed(FRACTION_DECIMAL_PLACES)
}

/**
 * Renders a stored decimal fraction as a percentage for display, e.g. `0.08500`
 * as `8.5%`. Returns an empty string when no rate is set.
 */
export const formatTaxRateFractionAsPercent = (fraction: string | null | undefined): string => {
  if (fraction === null || fraction === undefined || fraction.trim() === '') {
    return ''
  }
  const parsed = Number(fraction)
  if (!Number.isFinite(parsed)) {
    return ''
  }
  // Trailing zeros from the stored scale carry no meaning, so Number() drops them.
  return `${Number((parsed * 100).toFixed(3))}%`
}
